import {
  type PermitActionResult,
  extendPermit,
  requestPermit,
  startPermit,
  stopPermit,
} from '@/domains/jobs/api/permitApi';
import { queryKeys } from '@/lib/query-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

function useInvalidateJobDetail(jobId: number) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.detail(String(jobId)) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.activityLog(String(jobId)) }),
    ]);
}

export function useRequestPermit(jobId: number) {
  const invalidate = useInvalidateJobDetail(jobId);
  return useMutation<PermitActionResult, Error>({
    mutationFn: () => requestPermit({ jobId }),
    onSuccess: invalidate,
    meta: {
      successTitle: 'Permit Requested',
      successMessage: 'Permit request sent to SAP',
      errorTitle: 'Permit Request Failed',
    },
  });
}

export function useStartPermit(jobId: number, permitId: number | undefined) {
  const invalidate = useInvalidateJobDetail(jobId);
  return useMutation<PermitActionResult, Error>({
    mutationFn: () => {
      if (permitId == null) {
        throw new Error('Cannot start permit: permit not yet created');
      }
      return startPermit({ jobId, permitId, startDate: new Date().toISOString() });
    },
    onSuccess: invalidate,
    meta: {
      successTitle: 'Permit Started',
      successMessage: 'Start request sent to SAP',
      errorTitle: 'Could Not Start Permit',
    },
  });
}

export function useStopPermit(jobId: number, permitId: number | undefined) {
  const invalidate = useInvalidateJobDetail(jobId);
  return useMutation<PermitActionResult, Error>({
    mutationFn: () => {
      if (permitId == null) {
        throw new Error('Cannot stop permit: permit not yet created');
      }
      return stopPermit({ jobId, permitId, endDate: new Date().toISOString() });
    },
    onSuccess: invalidate,
    meta: {
      successTitle: 'Permit Stopped',
      successMessage: 'Stop request sent to SAP',
      errorTitle: 'Could Not Stop Permit',
    },
  });
}

// Extend takes the new permit expiry chosen by the user (ISO datetime). CORE
// maps it to SAP's extend API and confirms via a permitRequestType=extension
// inbound that updates the permit's endDate.
export function useExtendPermit(jobId: number, permitId: number | undefined) {
  const invalidate = useInvalidateJobDetail(jobId);
  return useMutation<PermitActionResult, Error, string>({
    mutationFn: (extendDate: string) => {
      if (permitId == null) {
        throw new Error('Cannot extend permit: permit not yet created');
      }
      return extendPermit({ jobId, permitId, extendDate });
    },
    onSuccess: invalidate,
    meta: {
      successTitle: 'Permit Extension Requested',
      successMessage: 'Extension request sent to SAP',
      errorTitle: 'Permit Extension Failed',
    },
  });
}
